/**
 * DOM menu controller (no Babylon). Owns the main menu, character select, and
 * the win/lose result box. The game's arena renders behind it as a backdrop.
 */
import { DEFAULT_CHARACTER, type CharacterId } from "@allstars/shared";
import type { Outcome } from "../game/localGame.js";

export interface Menu {
  onStart(cb: (char: CharacterId) => void): void;
  onReplay(cb: () => void): void;
  onBackToMenu(cb: () => void): void;
  showMenu(): void;
  showResult(outcome: Exclude<Outcome, "playing">): void;
  hideAll(): void;
}

const byId = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

export const createMenu = (): Menu => {
  const menu = byId("menu");
  const charSelect = byId("charSelect");
  const overlay = byId("overlay");
  const overlayTitle = byId("overlayTitle");
  const btnSingle = byId<HTMLButtonElement>("btnSingle");
  const btnStart = byId<HTMLButtonElement>("btnStart");
  const btnCharBack = byId<HTMLButtonElement>("btnCharBack");
  const resetBtn = byId<HTMLButtonElement>("resetBtn");
  const menuBtn = byId<HTMLButtonElement>("menuBtn");
  const cards = Array.from(charSelect.querySelectorAll<HTMLElement>("[data-char]"));

  let selected: CharacterId = DEFAULT_CHARACTER;
  let startCb: (c: CharacterId) => void = () => {};
  let replayCb: () => void = () => {};
  let backCb: () => void = () => {};

  const selectCard = (char: CharacterId): void => {
    selected = char;
    for (const c of cards) c.classList.toggle("selected", c.dataset.char === char);
  };
  selectCard(selected);
  for (const c of cards) {
    c.addEventListener("click", () => selectCard(c.dataset.char as CharacterId));
  }

  const show = (el: HTMLElement, on: boolean): void => {
    el.classList.toggle("hidden", !on);
  };

  btnSingle.addEventListener("click", () => {
    show(menu, false);
    show(charSelect, true);
  });
  btnCharBack.addEventListener("click", () => {
    show(charSelect, false);
    show(menu, true);
  });
  btnStart.addEventListener("click", () => {
    show(charSelect, false);
    show(menu, false);
    startCb(selected);
  });
  resetBtn.addEventListener("click", () => {
    show(overlay, false);
    replayCb();
  });
  menuBtn.addEventListener("click", () => {
    show(overlay, false);
    backCb();
  });

  return {
    onStart: (cb): void => {
      startCb = cb;
    },
    onReplay: (cb): void => {
      replayCb = cb;
    },
    onBackToMenu: (cb): void => {
      backCb = cb;
    },
    showMenu: (): void => {
      show(overlay, false);
      show(charSelect, false);
      show(menu, true);
    },
    showResult: (outcome): void => {
      overlayTitle.textContent = outcome === "won" ? "Sieg!" : "Niederlage";
      show(overlay, true);
    },
    hideAll: (): void => {
      show(menu, false);
      show(charSelect, false);
      show(overlay, false);
    },
  };
};
